import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import Index from "./pages/Index";

/**
 * The landing page stays in the main bundle — it is the most-requested route
 * and an extra round trip there would delay the hero (the LCP element).
 * Every other route is split out, which keeps the initial download to what the
 * first paint actually needs. Leaflet in particular is only pulled in by the
 * locations pages and no longer ships to visitors who never open one.
 */
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
import ScrollToTop from "./components/ScrollToTop";
import ScrollTopButton from "./components/ScrollTopButton";
import ReadingProgress from "./components/ReadingProgress";
import { QuoteFormProvider } from "./components/QuoteFormContext";

const queryClient = new QueryClient();

/**
 * LazyMotion + the `m` component ship only the animation features this site
 * actually uses (enter/exit, whileInView) instead of framer-motion's full
 * feature set. Same animations, a fraction of the JavaScript to parse.
 * It must sit above QuoteFormProvider, which animates its dialog.
 */
const App = () => (
  <QueryClientProvider client={queryClient}>
    <LazyMotion features={domAnimation} strict>
      <TooltipProvider>
        <QuoteFormProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ReadingProgress />
            {/* min-h-screen keeps the fallback the same height as a page, so a
                route swap does not collapse the layout and shift content. */}
            <Suspense fallback={<div className="min-h-screen" aria-busy="true" />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/about" element={<About />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/locations/:slug" element={<LocationDetail />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <ScrollTopButton />
          </BrowserRouter>
        </QuoteFormProvider>
      </TooltipProvider>
    </LazyMotion>
  </QueryClientProvider>
);

export default App;
